import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from './axios';
import qs from 'qs';
import { t } from 'i18next';
import { setCredentials, setUploadLicenceProgress } from '../slices/user';
import { RootState } from '../store';
import { AxiosProgressEvent, AxiosRequestConfig, AxiosResponse } from 'axios';

export const signIn = createAsyncThunk(
    'user/login',
    async ({ email, password, timezone }: { email: string; password: string; timezone: number }) => {
        const response = await axios.post('users/login', qs.stringify({ email, password, timezone }), {
            withCredentials: true,
        });

        if (response.status === 200 && typeof response.data === 'object') {
            return response.data;
        } else {
            if (response.data.indexOf('Account is disabled - SecurityException') !== -1) {
                throw t('login.error.accountDisabled');
            }

            if (response.data.indexOf('HTTP 401 Unauthorized - WebApplicationException') !== -1) {
                throw t('login.error.invalidUser');
            }
            if (response.data.indexOf('400 Bad Request') !== -1) {
                throw t('login.error.loginFailed');
            }
        }
        return false;
    },
);

export const relogIn = createAsyncThunk('user/relogin', async (_, { getState }) => {
    const state = getState() as RootState;
    const { credentials } = state.user;

    const response = await axios.post(
        'users/login',
        qs.stringify({ email: credentials.email, password: credentials.password }),
        {
            withCredentials: true,
        },
    );
    return response;
});

export const sendSupportMessage = createAsyncThunk(
    'user/support',
    async (
        {
            email,
            firstName,
            company,
            phone,
            message,
            emailCopy,
        }: {
            email: string;
            firstName: string;
            company?: string;
            phone?: string;
            message: string;
            emailCopy: number;
        },
        { getState },
    ) => {
        const state = getState() as RootState;
        const { language } = state.user;
        const response = await axios.post('users/support', {
            language,
            email,
            firstName,
            company,
            phone,
            message,
            emailCopy,
        });

        // console.log('sendSupportMessage', response);
        if (response.status === 200 && typeof response.data === 'object') {
            return response.data;
        } else {
            throw 'error';
        }
    },
);

export const sendCancelSubscription = createAsyncThunk(
    'user/support',
    async (
        {
            imei,
            plan_name,
            model,
            subject,
            message,
        }: {
            imei: string;
            plan_name: string;
            model: string;
            subject: string;
            message: string;
        },
        { getState },
    ) => {
        const state = getState() as RootState;
        const { email, name } = state.user.data;
        const response = await axios.post('users/cancel-subscription', {
            imei,
            model,
            subject,
            plan_name,
            email,
            message,
            name,
        });

        // console.log('sendCancelSubscription', response);
        if (response.status === 200 && typeof response.data === 'object') {
            return response.data;
        } else {
            throw 'error';
        }
    },
);

export const uploadUserPhoto = createAsyncThunk('user/upload-image', async ({ file }: { file: Blob }) => {
    const formData = new FormData();
    formData.append('file', file, 'user-image.jpg');

    const response = await axios.post(`users/upload/photo?aws=1`, formData);

    // console.log('uploadUserPhoto', response.data);
    if (response.status === 200 && typeof response.data === 'object' && response.data.status === 200) {
        return true;
    } else {
        return false;
    }
});

export const updateProfile = createAsyncThunk('users/update', async ({ data }: { data: FormData }) => {
    const response = await axios.post('users/update', data);

    if (response.status === 200 && typeof response.data === 'object') {
        return response.data.data;
    } else {
        const { data, status, message } = response.data;
        throw {
            code: status,
            message: message,
            data: data,
        };
    }
});

export const changePassword = createAsyncThunk(
    'users/change-password',
    async ({ data, password }: { data: FormData; password: string }, { dispatch, getState }) => {
        const response = await axios.post('users/change-password', data);

        const state = getState() as RootState;
        const { rememberMe, credentials } = state.user;

        if (response.data.status === 200) {
            if (rememberMe) {
                dispatch(setCredentials({ ...credentials, password }));
            }
        } else {
            throw response.data;
        }
    },
);

export const getUser = createAsyncThunk('user/get', async () => {
    const response = await axios.get('users/profile');

    if (response.status === 200 && typeof response.data === 'object') {
        return response.data;
    } else {
        throw t('error');
    }
});

export const checkUserForgot = createAsyncThunk('user/forgot-check', async ({ param }: { param: string }) => {
    const response = await axios.get(`users/forgot-password?${param}`);

    return response;
});

export const checkUserForgotCode = createAsyncThunk(
    'user/forgot-check-code',
    async ({ email, code }: { email: string; code: string }) => {
        const response = await axios.get(`users/validate`, { params: { email, code } });

        return response;
    },
);
export const resetPassword = createAsyncThunk(
    'user/reset-password',
    async ({ formData, code }: { formData: FormData; code: string }) => {
        const response = await axios.post(`users/reset-password/${code}`, formData);

        return response;
    },
);

export const deleteAccount = createAsyncThunk('user/delete', async () => {
    const response = await axios.delete('users/trash/account');

    return response.data;
});

export const deleteLicence = createAsyncThunk('user/delete-licence', async ({ id }: { id: number }) => {
    const response = await axios.delete(`users/licences/${id}?aws=1}`);

    return response.data;
});

export const getLicences = createAsyncThunk('user/get-licences', async () => {
    const response = await axios.get('users/licences');

    if (response.status === 200 && typeof response.data === 'object') {
        return response.data.data;
    } else {
        throw t('error');
    }
});

export const updateUserSetting = createAsyncThunk(
    'user/update-setting',
    async ({ setting, value }: { setting: string; value: string | null }) => {
        const response = await axios.put(`users/update-setting?time=${new Date().getTime()}`, { setting, value });

        // console.log('updateUserSetting', response);
        if (response.status === 200 && typeof response.data === 'object') {
            return { setting, value };
        } else {
            throw t('error');
        }
    },
);

export const uploadLicence = createAsyncThunk(
    'user/licence-upload',
    async ({ formData }: { formData: FormData }, { dispatch }) => {
        try {
            const config: AxiosRequestConfig = {
                onUploadProgress: function (progressEvent: AxiosProgressEvent) {
                    const percentage = Math.round((progressEvent.loaded * 100) / (progressEvent?.total ?? 1));
                    dispatch(setUploadLicenceProgress(percentage));
                },
            };

            const response = await axios.post<AxiosResponse>(`users/upload/licence?aws=1`, formData, config);
            // console.log('uploadLicence', response.data);
            dispatch(setUploadLicenceProgress(0));

            if (response.status === 200 && typeof response.data === 'object') {
                return response.data;
            } else {
                throw new Error(response.data?.data.message);
            }
        } catch (error) {
            console.log('uploadLicence e', error);
            dispatch(setUploadLicenceProgress(0));
            throw error;
        }
    },
);
